import React, { useCallback, useEffect, useState } from 'react';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { AHeaderTag } from '../../atoms/AHeader/index';
import { MAccordionItem } from '../../molecules/MAccordionItem/index';
import { MAccordionHeader } from '../../molecules/MAccordionHeader/index';
import type { OAccordionProps } from './OAccordion.types';
import { Wrapper } from './OAccordion.styles';
import MSlot from '../../molecules/MSlot';

// TODO: Get I18n keys for these hardcoded tokens
const TEXT_I18N_EXPAND_ALL = 'Expand All';
const TEXT_I18N_COLLAPSE_ALL = 'Collapse All';

const OAccordionConfig = {
  emptyLabel: 'OAccordion',
  isEmpty: (props: OAccordionProps) => {
    const headerText = props.mAccordionHeader?.aHeader?.text;
    return !headerText;
  },
  resourceType: 'mi-aem-utils/components/content/o-accordion',
};

export const OAccordionMarkup: React.FC<OAccordionProps> = props => {
  const { mAccordionHeader = {}, cqPath: _cqPath } = props;
  const [allExpanded, setAllExpanded] = useState(false);
  const [expandedItems, setExpandedItems] = useState<{ [key: number]: boolean }>({});

  const onExpandAll = useCallback(() => {
    const newExpandedState = !allExpanded;
    setAllExpanded(newExpandedState);

    // Update all items to the new state
    const newExpandedItems: { [key: number]: boolean } = {};
    if (props.mAccordionItems) {
      props.mAccordionItems.forEach((_, index) => {
        newExpandedItems[index] = newExpandedState;
      });
    }
    setExpandedItems(newExpandedItems);
  }, [allExpanded, props.mAccordionItems]);

  const handleItemToggle = useCallback(
    (index: number, isExpanded: boolean) => {
      setExpandedItems(prev => ({
        ...prev,
        [index]: isExpanded,
      }));

      // Check if all items are expanded to update the "Expand All" state
      if (props.mAccordionItems) {
        const newExpandedItems = { ...expandedItems, [index]: isExpanded };
        const allItemsExpanded = props.mAccordionItems.every((_, idx) => newExpandedItems[idx]);
        setAllExpanded(allItemsExpanded);
      }
    },
    [expandedItems, props.mAccordionItems]
  );

  useEffect(() => {
    if (props.isAuthorMode) {
      onExpandAll();
    }
  }, []);

  const toggleText = allExpanded ? TEXT_I18N_COLLAPSE_ALL : TEXT_I18N_EXPAND_ALL;
  return (
    <Wrapper>
      <MAccordionHeader
        aHeader={{ element: AHeaderTag.h2, ...mAccordionHeader.aHeader }}
        aLinkStandalone={{
          onClick: onExpandAll,
          text: toggleText,
          'aria-label': toggleText,
          ...mAccordionHeader.aLinkStandalone,
          href: undefined,
        }}
        alignment={mAccordionHeader.alignment}
        link={mAccordionHeader.link === 'true'}
        className="pb-4"
      />

      {/* Render accordion items */}
      {props.mAccordionItems && props.mAccordionItems.length > 0 ? (
        props.mAccordionItems.map((item, index) => (
          <MAccordionItem
            key={`accordion-item-${index}`}
            {...item}
            expanded={expandedItems[index]}
            onToggle={() => handleItemToggle(index, !expandedItems[index])}
          >
            <MSlot
              pagePath={props?.pagePath}
              itemPath={`${props?.itemPath}/${item.nodeName || ''}`}
              isAuthorMode={props.isAuthorMode}
              allowedComponents={props?.allowedComponents}
              cqItems={(item.nodeName && props?.cqItems?.[item.nodeName]?.[':items']) || {}}
              model={(item.nodeName && props?.cqItems?.[item.nodeName]) || {}}
            />
          </MAccordionItem>
        ))
      ) : (
        <div>No accordion items available</div>
      )}
    </Wrapper>
  );
};

export const OAccordionEditableComponent: React.FC<OAccordionProps> = props => (
  <EditableComponent config={OAccordionConfig} {...props}>
    <OAccordionMarkup {...props} />
  </EditableComponent>
);

export { OAccordionEditableComponent as OAccordion };
